import { axiosInstance } from "../../../api/axiosInstance.tsx";

// Interface untuk Building Assessment Search Parameters
export interface BuildingAssessmentSearchParams {
  keyword?: string;
  officeId?: string;
  assessmentDate?: string;
  picName?: string;
  assessmentType?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  order?: string;
}

// Interface untuk Building Assessment Data
export interface BuildingAssessmentData {
  id?: string;
  officeId: string;
  assessmentDate: string;
  picName: string;
  assessmentType: string;
  status: string;
}

// Interface untuk Assessment Detail Search Parameters
export interface AssessmentDetailSearchParams {
  assessmentId?: string;
  subSection?: string;
  damageItem?: string;
  condition?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  order?: string;
}

// Interface untuk Assessment Detail Data
export interface AssessmentDetailData {
  id?: string;
  assessmentId: string;
  subSection: string;
  damageItem: string;
  condition: string;
  length?: number;
  width?: number;
  height?: number;
  notes: string;
  evidenceFile?: File | null;
}

// Get all Building Assessments
export const getBuildingAssessments = async (params: BuildingAssessmentSearchParams = {}) => {
  const response = await axiosInstance.get("/building/assessment/search_assessments", {
    params: {
      limit: params.limit || 10,
      offset: params.offset || 0,
      ...params
    },
  });
  return response.data;
};

// Get single Building Assessment by ID
export const getBuildingAssessment = async (id: string) => {
  const response = await axiosInstance.get(`/building/assessment/view_assessment/${id}`);
  return response.data;
};

// Create new Building Assessment
export const createBuildingAssessment = async (data: BuildingAssessmentData) => {
  const response = await axiosInstance.post("/building/assessment/create_assessment", data);
  return response.data;
};

// Update Building Assessment
export const updateBuildingAssessment = async (id: string, data: BuildingAssessmentData) => {
  const response = await axiosInstance.put(`/building/assessment/update_assessment/${id}`, data);
  return response.data;
};

// Delete Building Assessment
export const deleteBuildingAssessment = async (id: string) => {
  const response = await axiosInstance.delete(`/building/assessment/delete_assessment/${id}`);
  return response.data;
};

// Upload Evidence Photo for Assessment Detail
export const uploadAssessmentEvidence = async (assessmentDetailId: string, file: File) => {
  const formData = new FormData();
  formData.append("evidence", file);

  const response = await axiosInstance.post(
    `/building/assessment-detail/upload_evidence/${assessmentDetailId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

// Fetch Evidence Photo Blob
export const fetchAssessmentEvidenceBlob = async (filename: string): Promise<string | null> => {
  try {
    const token = localStorage.getItem("token");

    const response = await axiosInstance.get(`/building/assessment-detail/evidence/${filename}`, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const objectUrl = URL.createObjectURL(response.data);
    return objectUrl;
  } catch (error) {
    console.error("Gagal mengambil foto evidence:", error);
    return null;
  }
};

// ==================== ASSESSMENT DETAILS ====================

// Get Assessment Details
export const getAssessmentDetails = async (params: AssessmentDetailSearchParams = {}) => {
  const response = await axiosInstance.get("/building/assessment-detail/search_assessment_details", {
    params: {
      limit: params.limit || 1000000,
      offset: params.offset || 0,
      ...params
    },
  });
  return response.data;
};

// Get single Assessment Detail by ID
export const getAssessmentDetail = async (id: string) => {
  const response = await axiosInstance.get(`/building/assessment-detail/view_assessment_detail/${id}`);
  return response.data;
};

// Create new Assessment Detail
export const createAssessmentDetail = async (data: AssessmentDetailData) => {
  const formData = new FormData();
  
  // Append all data fields to formData
  Object.entries(data).forEach(([key, value]) => {
    if (key !== 'evidenceFile' && value !== undefined && value !== null) {
      formData.append(key, value.toString());
    }
  });
  
  // Append evidence file if exists
  if (data.evidenceFile) {
    formData.append("evidence", data.evidenceFile);
  }

  const response = await axiosInstance.post(
    "/building/assessment-detail/create_assessment_detail",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

// Update Assessment Detail
export const updateAssessmentDetail = async (id: string, data: AssessmentDetailData) => {
  const formData = new FormData();
  
  // Append all data fields to formData
  Object.entries(data).forEach(([key, value]) => {
    if (key !== 'evidenceFile' && value !== undefined && value !== null) {
      formData.append(key, value.toString());
    }
  });
  
  // Append evidence file if exists
  if (data.evidenceFile) {
    formData.append("evidence", data.evidenceFile);
  }

  const response = await axiosInstance.put(
    `/building/assessment-detail/update_assessment_detail/${id}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

// Delete Assessment Detail
export const deleteAssessmentDetail = async (id: string) => {
  const response = await axiosInstance.delete(`/building/assessment-detail/delete_assessment_detail/${id}`);
  return response.data;
};

// ==================== STATISTICS & REPORTS ====================

// Get Assessment Statistics
export const getAssessmentStatistics = async (params: {
  startDate?: string;
  endDate?: string;
  officeId?: string;
  assessmentType?: string;
}) => {
  const response = await axiosInstance.get("/building/assessment/statistics", {
    params,
  });
  return response.data;
};

// Export Assessment Report
export const exportAssessmentReport = async (assessmentId: string, format: 'pdf' | 'excel' = 'pdf') => {
  const response = await axiosInstance.get(`/building/assessment/export_report/${assessmentId}`, {
    params: { format },
    responseType: 'blob'
  });
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  
  const fileName = `assessment_report_${assessmentId}.${format}`;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  
  return fileName;
};

// ==================== DASHBOARD DATA ====================

// Get Dashboard Summary
export const getDashboardSummary = async () => {
  const response = await axiosInstance.get("/building/assessment/dashboard_summary");
  return response.data;
};

// Get Assessments by Status
export const getAssessmentsByStatus = async (params: {
  startDate?: string;
  endDate?: string;
  officeId?: string;
}) => {
  const response = await axiosInstance.get("/building/assessment/by_status", {
    params,
  });
  return response.data;
};

// Get Assessments by Type
export const getAssessmentsByType = async (params: {
  startDate?: string;
  endDate?: string;
}) => {
  const response = await axiosInstance.get("/building/assessment/by_type", {
    params,
  });
  return response.data;
};